/**
 * AG Survey Theme Extension Loader (Tier 0).
 *
 * Eagerly parsed on every page → MUST stay tiny (no Preact, no fetch, no DOM ops).
 * Job: check pre-injected metafield + URL → decide if/when the main bundle loads.
 *
 * Liquid (app-data.liquid) sets:
 *   - window.AVADA_SURVEY = {_disabled: true, ...}   if no active surveys
 *   - window.AVADA_SURVEY = {surveys, quizzes, ...}  if shop needs full bundle
 *
 * BFS perf gate (Built for Shopify):
 *   On normal storefront pages the heavy main bundle (Preact mount + survey render)
 *   is injected ONLY after the FIRST real user interaction (scroll/mouse/touch/key/
 *   pointer/click). A cold Lighthouse run performs NO interaction → only this ~2KB
 *   loader executes → app's Lighthouse impact ≈ 0. Real users virtually always
 *   interact, so the popup still appears with a negligible delay.
 *   No requestIdleCallback / no setTimeout fallback here on purpose — those fire
 *   during a no-interaction Lighthouse trace and were the root cause of the score drop.
 *
 * Intent-driven URLs (quiz page / embed / preview) load immediately — the visitor
 * navigated specifically for that content, and those pages are not the homepage
 * measured by BFS.
 *
 * Cache strategy: `?v=${Date.now()}` query string — each page load fetches
 * a fresh URL. Chosen so scripttag deploys propagate to merchants instantly
 * with zero coordination (no extension redeploy, no CDN purge). Trade-off:
 * 0% browser/CDN cache hit on this bundle — accepted for instant rollout.
 */
(function() {
  // const BASE_URL = 'https://ag-survey-staging-1.firebaseapp.com/scripttag';
  // const BASE_URL = 'https://localhost:3001/scripttag';
  const BASE_URL = 'https://cdn-sea-survey.avada.io/scripttag';
  const INTERACTION_EVENTS = [
    'scroll',
    'mousemove',
    'touchstart',
    'keydown',
    'pointerdown',
    'click'
  ];

  const ctx = window.AVADA_SURVEY;
  if (!ctx || ctx._disabled) return;

  const path = (window.location && window.location.pathname) || '';
  const search = (window.location && window.location.search) || '';
  const isIntentDriven =
    path.indexOf('/pages/quiz/') !== -1 ||
    search.indexOf('survey_embed=') !== -1 ||
    search.indexOf('previewId=') !== -1;
  const hasSurveys = Array.isArray(ctx.surveys) && ctx.surveys.length > 0;

  // Nothing to do on this page.
  if (!isIntentDriven && !hasSurveys) return;

  let injected = false;

  /** Detach all interaction listeners (called once the bundle is injected). */
  function removeListeners() {
    INTERACTION_EVENTS.forEach(function(evt) {
      window.removeEventListener(evt, onInteraction, true);
    });
  }

  /**
   * Inject on the first TRUSTED (real user) event. Synthetic/programmatic events
   * (event.isTrusted === false) — e.g. theme auto-scroll or a Lighthouse/PageSpeed
   * audit — are ignored, so a BFS audit never loads the heavy bundle.
   * @param {Event} e Interaction event.
   */
  function onInteraction(e) {
    if (e && e.isTrusted === false) return;
    injectCore();
  }

  /** Inject the heavy main bundle once (async), then detach listeners. */
  function injectCore() {
    if (injected) return;
    injected = true;
    removeListeners();

    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = BASE_URL + '/avada-survey-main.min.js?v=' + new Date().getTime();
    const first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(s, first);
    } else {
      (document.head || document.documentElement).appendChild(s);
    }
  }

  // Quiz / embed / preview → user intent is explicit, load now.
  if (isIntentDriven) {
    injectCore();
    return;
  }

  // Storefront survey → defer bundle injection until first real user interaction.
  // No `once` — we keep listening until a TRUSTED event arrives (synthetic events
  // are ignored above), then injectCore() removes all listeners itself.
  INTERACTION_EVENTS.forEach(function(evt) {
    window.addEventListener(evt, onInteraction, {passive: true, capture: true});
  });
})();
